/**
 * The enterprise app shell (STEP 5) — composes Header, Sidebar,
 * page content (`<Outlet/>`), StatusBar, QuickSearch,
 * the session-timeout warning, and the offline banner into one
 * layout every protected route renders inside
 * (`routes/AppRouter.tsx`).
 */
import { useCallback, useEffect, type JSX } from 'react';
import { Box } from '@mui/material';
import { Outlet, useLocation } from 'react-router-dom';
import { Header, HEADER_HEIGHT } from './Header';
import { Sidebar } from './Sidebar';
import { StatusBar } from './StatusBar';
import { QuickSearch } from './QuickSearch';
import { SessionTimeoutWarning } from './SessionTimeoutWarning';
import { OfflineBanner } from '@components/feedback/OfflineBanner';
import { CounterSelectionGate, useCounterSelection } from './CounterSelectionGate';
import { GlobalLoader } from '@components/feedback/GlobalLoader';
import { useSessionTimeout } from '@hooks/useSessionTimeout';
import { useAuth } from '@hooks/useAuth';
import { useAutoSelectSingleHospitalBranch } from '@hooks/useAutoSelectSingleHospitalBranch';
import { getBreadcrumbTrail, hueOf } from '@config/navigation-registry';
import { useRecentPagesStore } from '@store/recent-pages.store';

/**
 * Routes that own their whole viewport: NO page padding and NO page scroll.
 *
 * SINCE 2026-09-06 THIS LIST NO LONGER HAS ANYTHING TO DO WITH BREADCRUMBS.
 * The crumb row was removed from every route, so the per-entry rationale below
 * — most of it written to justify dropping the crumb on one more screen — is
 * now HISTORY, not behaviour. What the list still decides is real and
 * unchanged: a workstation manages its own bands, so the shell must not add a
 * gutter around it or a scrollbar that could carry its action footer off-screen.
 *
 * Kept as a prefix list rather than a flag on the page because the shell has to
 * decide before the page renders — the padding is the shell's own, and a page
 * cannot un-pad its parent without negative margins that break the moment the
 * shell changes.
 *
 * Exported since ADR-097 so the LAB stage routes can be asserted against THIS
 * list rather than a copy of it.
 */
export const FULL_BLEED_ROUTE_PREFIXES = [
  '/op-billing',
  '/billing/counter',
  // The two family desks render the SAME workstation as `/billing/counter`,
  // now behind their own tab strip. They were left off this list, so one
  // billing screen wore a breadcrumb and the shell's padding while the other
  // did not — and the breadcrumb restated a page that names itself in its own
  // dark header band a row below.
  '/billing/laboratory',
  '/billing/radiology',
  '/ip/register',
  '/ip-billing',
  // IP Lookup, for exactly the reason the four screens around it are here: the
  // page states what it is in its own dark band a row below, the sidebar has
  // already highlighted the row, and the crumb spent a full line of a
  // fixed-height workstation saying "Front Office / IP Lookup" above a header
  // that reads "IP Lookup".
  '/ip/lookup',
  // The IP command centre. Same reason as the workstations either side of it:
  // it names itself in its own dark header band a row below, and the breadcrumb
  // spent a full row restating "Front Office / IP Dashboard" above a page the
  // sidebar had already highlighted.
  '/ip/dashboard',
  // ADR-097 — the six LAB stages. Same reason as the workstations above: each
  // page names itself in its own header a row below, the sidebar has already
  // highlighted the leaf, and the crumb spent a full row saying
  // "Diagnostics / Laboratory / Orders" over a page whose h1 reads "Orders".
  // `/laboratory/` with the trailing slash, so the master workspace at bare
  // `/laboratory` keeps its breadcrumb.
  /*
    THE WHOLE LAB WORKSPACE, not just its stage routes (desk, 2026-08-27:
    "remove breadcrumb").

    This entry was `/laboratory/` WITH a trailing slash, which matched
    `/laboratory/orders` and every other stage but deliberately missed the bare
    `/laboratory` the Dashboard and the master tabs live on. So one screen in
    the module wore a crumb reading "Diagnostics / Laboratory / Masters / Lab
    Dashboard" above a page whose own header says Laboratory — four words of
    navigation restating a sidebar row that is already highlighted.

    Dropping the slash covers both. It also takes the page's ONLY MUI with it:
    the crumb bar is `Stack` + MUI `Breadcrumbs`, and the laboratory feature
    itself imports none.
  */
  /*
    The Main Counter names itself in its own header band and carries its own tab
    rail, so a crumb reading "Billing & Finance / Main Counter" above a page
    titled Main Counter spends a row of a fixed-height workstation saying
    nothing. Same reason as every workstation above it in this list.
  */
  '/counter',
  '/laboratory',
  /*
    THE ADMIN CONSOLE, for both of the reasons everything above it is here —
    and for a third that was pure waste.

    1. It names itself. The console draws its own context bar ("All settings ›
       People & Access › Users") and the panel below draws its own `PageHeader`.
    2. The breadcrumb was ALREADY suppressed on this route
       (`Breadcrumbs.tsx`'s `NO_BREADCRUMB_PREFIXES`) — but only the crumb was.
       The ROW survived: a `px:3 py:0.75` strip with a bottom border, holding
       two components that both render `null` here. An empty bordered band
       under the header, on every admin screen, forever.
    3. `overflow: hidden` is what lets the console's grid fill the window. With
       the shell scrolling, the workspace had no definite height to resolve
       against and fell back to `EnterpriseDataGrid`'s 480px default, which is
       the dead space below the last row the desk reported.

    The console supplies its own padding in exchange — see `AdminConsole.tsx`.
  */
  '/administration',
  // The trailing slash matters: `/patients` (the search grid) keeps its
  // breadcrumb, while the profile and the registration desk — both fixed-height
  // workstations with their own header band — do not.
  '/patients/',
  '/appointments',
  /*
    REPORTS & ANALYTICS (desk, 2026-09-06: "remove breadcrumb, use full screen
    width, no need of side blank spaces").

    Both halves of that are this one entry, which is why the request is a line
    here rather than an edit to the page: `isFullBleed` drops the crumb ROW —
    not just the crumb, the whole bordered strip — AND the shell's `px: 3`
    gutter, so the report meets the chrome.

    It earns the place for the same reason as every workstation above it. The
    page carries its own tab rail naming the report, the sidebar leaf that
    opened it is already highlighted, and the crumb spent a full row of a
    fixed-height screen saying "Reports & Analytics / Registration &
    Appointments" over a tab already reading "Registration & Appointments".

    A report is also the screen that most wants the width: the doctor table is
    thirteen columns and the receipt list is eleven, and 48px of gutter is a
    column of each.
  */
  '/reports',
  /*
    The Front Desk dashboard at `/front-desk/dashboard`, which renders the SAME
    report body `/reports?tab=front-desk` does. It has to be here for exactly
    that reason: one component behind two routes, where one route is full-bleed
    and the other is not, is a screen whose register is 48px narrower and whose
    internal scroller fights a page scroller depending on how it was reached.

    A PREFIX, so the section can grow further routes without another entry —
    and deliberately `/front-desk` rather than `/front-desk/`, matching the
    `/laboratory` convention the lab stage routes already assert against.
  */
  '/front-desk',
  /*
    HR ATTENDANCE/LEAVE/ROSTER/COMPLIANCE (2026-09-16: "every sheet having more
    blank spaces at borders... fix the full screen"). Same shape as every
    workstation above it — its own toolbar names what tab is showing, the
    sidebar leaf is already highlighted, and the shell's `px: 3, pt: 2, pb: 3`
    gutter was stacking on top of the page's own padding, doubling it exactly
    the way Reports & Analytics did before this list existed.

    `HrAttendancePage` now owns its own scroll region (`overflow-auto` on one
    inner div) rather than relying on the shell's, matching the pattern this
    file already documents above: a full-bleed route sets `overflow: hidden`
    at the shell, so the page underneath must be the thing that scrolls.
  */
  '/hr',
  /*
    MODIFICATIONS (desk, 2026-10-07: "modification user will use only mobile"). Full-bleed so a phone gets the whole 390px instead of 342
    between the shell's 24px gutters, and so the page can pin its OP / IP tabs while the cards scroll under them. The page owns its
    scroll and its own padding (`ModificationsCorrectionPage`).
  */
  '/modifications',
];

export function AppLayout(): JSX.Element {
  const { logout } = useAuth();
  const location = useLocation();
  const recordVisit = useRecentPagesStore((state) => state.recordVisit);
  useAutoSelectSingleHospitalBranch();

  const handleTimeout = useCallback(() => {
    logout();
  }, [logout]);

  const { warningVisible, dismissWarning } = useSessionTimeout({ onTimeout: handleTimeout });
  // Called unconditionally, with the other hooks — the early return it feeds
  // sits below every hook in this component, never between them.
  const counterSelection = useCounterSelection();

  const isFullBleed = FULL_BLEED_ROUTE_PREFIXES.some((prefix) => location.pathname.startsWith(prefix));
  // The DEEPEST crumb, which is the row the sidebar marks selected. Falls back
  // to grey — the neutral family — on a route the registry does not know, so an
  // unlisted page is plain rather than arbitrarily coloured.
  const trail = getBreadcrumbTrail(location.pathname + location.search);
  const pageHue = trail.length > 0 ? hueOf(trail[trail.length - 1]?.id ?? '') : 'grey';

  useEffect(() => {
    const trail = getBreadcrumbTrail(location.pathname + location.search);
    const leaf = trail[trail.length - 1];
    if (leaf?.path) {
      recordVisit({ id: leaf.id, label: leaf.label, path: leaf.path });
    }
  }, [location.pathname, location.search, recordVisit]);

  // THE COUNTER COMES BEFORE THE APPLICATION.
  //
  // Rendered INSTEAD OF the header, sidebar and routed page — not over them —
  // so a biller who has not named their desk has nothing behind the dialog to
  // reach. Anyone who does not take money, and anyone who has already chosen,
  // falls straight through (`required` is false for them and no request is
  // even issued). See `CounterSelectionGate`'s own header for why this blocks.
  if (counterSelection.required) {
    return (
      <Box display="flex" minHeight="100vh">
        <OfflineBanner />
        <CounterSelectionGate />
      </Box>
    );
  }

  return (
    <Box display="flex" minHeight="100vh">
      <OfflineBanner />
      <GlobalLoader />
      <Header />
      <Sidebar />
      {/* `minWidth: 0` overrides the flex-item default of `min-width: auto` — without it, this
          column refuses to shrink below its content's natural width and overflows past the
          sidebar on narrow viewports instead of wrapping (confirmed live, Stage RC1). */}
      {/* `height` (not `minHeight`) + `overflow: hidden` — this is what actually makes the
          Box below the real, bounded scrolling container `StickyActionFooter.tsx` already
          assumed it was: previously `main` had no capped height at all, so the whole
          document scrolled and any `position: sticky` element inside a short page (e.g. a
          near-empty Billing Workstation cart) just sat wherever its normal flow left it
          instead of being pushed down to the true bottom of the screen (real feedback,
          asked three times). */}
      {/*
        THE PAGE WEARS ITS SECTION'S COLOUR.

        The hue comes from the SAME `hueOf()` the sidebar row uses, resolved
        from the same breadcrumb trail that decides which row is selected — so
        the tint on the screen and the tint on the rail cannot disagree. An
        operator sees one colour, in two places, meaning one thing.

        A TINT, not a fill: `--nx-<hue>-tint` is the 50-step of the ramp. Status
        still arrives as a filled container plus an icon plus a heavier border
        (see `design-tokens.css`), so a red SECTION and a red ALERT stay
        separable by form, which is what WCAG 1.4.1 requires.
      */}
      <Box
        component="main"
        flexGrow={1}
        minWidth={0}
        display="flex"
        flexDirection="column"
        sx={{
          marginTop: `${HEADER_HEIGHT}px`,
          height: `calc(100vh - ${HEADER_HEIGHT}px)`,
          overflow: 'hidden',
          bgcolor: `var(--nx-${pageHue}-tint)`,
        }}
      >
        {/*
          THE BREADCRUMB ROW IS GONE, ON EVERY ROUTE (desk, 2026-09-06:
          "remove MUI and breadcrumb in all pages").

          It had been removed route by route for two years —
          `FULL_BLEED_ROUTE_PREFIXES` grew an entry, and a paragraph of
          justification, every time a desk noticed it: the lab stages, the
          billing counters, IP lookup, IP dashboard, the admin console. The
          reason was the same one every time, and it is general, not per-route:
          the sidebar has already highlighted the row, the page names itself in
          its own header, and the crumb spent a full line of vertical space
          restating both.

          NOTHING WAS LOST WITH IT. `BreadcrumbActionBar` read
          `breadcrumb.store`'s `actions`, which no page has ever populated —
          `usePageActions()` had zero callers — so it rendered `null` on every
          screen in the product. Verified before deleting, because a strip like
          this one HAS carried real function before (see the LIS right-rail
          removal, where the rail held the only path to sample rejection).

          `FULL_BLEED_ROUTE_PREFIXES` STAYS. It still decides page padding and
          whether the shell scrolls, which is a separate question from whether a
          crumb is drawn — a workstation manages its own bands either way, and
          ADR-097's tests assert lab's stage routes against that list.
        */}
        <Box
          flexGrow={1}
          minHeight={0}
          sx={
            isFullBleed
              ? // No padding and NO page scroll: a workstation manages its own
                // bands and scrolls inside them. A scrollbar here would let the
                // action footer be scrolled off, which is the one thing that
                // must always be reachable.
                { overflow: 'hidden' }
              : { px: 3, pt: 2, pb: 3, overflowY: 'auto' }
          }
        >
          <Outlet />
        </Box>
        <StatusBar />
      </Box>
      <QuickSearch />
      <SessionTimeoutWarning open={warningVisible} onStayLoggedIn={dismissWarning} onLogout={handleTimeout} />
    </Box>
  );
}
